import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Page400, Page404, Page500 } from '../ErrorPages/Errors';
import DynamicFormPage from '../DynamicFormPage/DynamicFormPage';
import './FormContainer.css';
import { API_URL } from '../../constants';
import * as Sentry from '@sentry/react';

type FormField = {
  position: number;
  id: string;
  label: string;
  extId?: boolean;
  required: boolean;
  validation?: {
    name: string;
    pattern: string;
    message: string;
  };
  type: string;
  options?: {
    value: string;
    label: string;
  }[];
  placeholder?: string;
};

type DynamicFormParams = {
  slug: string;
  logoImageUrl: string;
  faviconUrl: string;
  formFields: FormField[];
  clientWebsite: string;
  componentFillColor: string;
  componentFontColor: string;
  backgroundColor: string;
  buttonColor: string;
  buttonCtaText: string;
  buttonCtaColor: string;
  templateId: string;
  footerContent: string;
  footerColor: string;
  footerEnabled: boolean;
  queryParams: any[]
  dataProcessingConsentRequired: boolean;
  dataProcessingConsentText: string;
}

const PARAM_ALLOWLIST = [
  'groupTag', 'groupTags',
  // Google Analytics
  '_gl',
  // UTM
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  // Hubspot
  '__hstc', '__hssc', '__hsfp', '_hsenc', '_hsmi',
  // Instagram / Facebook
  'fbclid',
]

const FormContainer = () => {
  const { slug } = useParams();
  const [error, setError] = useState(null);
  const [formTemplate, setFormTemplate] = useState<DynamicFormParams | null>(null);
  const [permittedParams, setPermittedParams] = useState<any>([])
  const [missingParams, setMissingParams] = useState<any>([])

  const search = window.location.search;
  const searchParams = new URLSearchParams(search);
  const queryParams =
    Array.from(searchParams.entries()).map(([key, value]) => {
      if (key === 'groupTags') {
        const uniqueGroupTags = Array.from(new Set(value.split(',')));
        return { [key]: uniqueGroupTags };
      }
      return { [key]: value || null };
    }).filter((param) => Object.values(param)[0] !== null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/internal/v1/dynamic_form_templates/${slug}`,
        );

        const { data: { attributes: formTemplate } } = response.data || {}

        setFormTemplate(formTemplate || {});
        setPermittedParams(
          [...formTemplate?.queryParams,  ...PARAM_ALLOWLIST.map(param => ({id: param, name: param, required: false}))]
        );
        setError(null);
      } catch (err: any) {
        Sentry.captureException(err);
        setError(err.response?.status || 500);
      }
    };

    setFormTemplate(null);
    setError(null);

    fetchData();
  }, [slug]);

  useEffect(() => {
    if (permittedParams.length > 0) {
      const requiredParams = permittedParams.filter((param: { required: boolean }) => {
        return param.required
      });

      const missing = requiredParams.filter((param: { id: string }) => {
        return !queryParams.some((p) => Object.keys(p)[0] === param.id);
      });

      if (JSON.stringify(missing) !== JSON.stringify(missingParams)) {
        setMissingParams(missing);
      }
    }
  }, [queryParams, permittedParams]);

  if (!slug || error === 404) {
    return <Page404 />
  }

  if (error === 400 || missingParams.length > 0) {
    return <Page400 />
  }

  if (error === 500 || error === 401) {
    return <Page500 />
  }

  if (!formTemplate) {
    return (
      <div className="loader-container">
        <div className="loader" role="status" aria-label="Loading"></div>
      </div>
    );
  }

  const recognisedQueryParams = queryParams.filter((param) =>
    permittedParams.some((p: { id: string }) => p.id === Object.keys(param)[0])
  );

  return (
    <DynamicFormPage attributes={formTemplate} queryParams={recognisedQueryParams} />
  );
};

export default FormContainer;
